import { motion } from "framer-motion";
import { CalendarDays } from "lucide-react";
import CategoryHero from "./CategoryHero";
import NewsTicker from './NewsTicker';
import { FEATURED_BUSINESS_DEALS, FeaturedDealCard } from "./Deals";
import { EVENTS, EventCard, isEventUpcoming, isEventInMonth, parseLocal } from "./Events";
import { useManagedCollection, slugify } from "../admin/useManagedCollection";

// Pull every deal from the Deals page that either has no expiry date at all,
// or expires on/before July 31, 2026.
const CUTOFF = "2026-07-31";
const HOT_DEALS = FEATURED_BUSINESS_DEALS.filter(
  (d) => !d.expiryDate || d.expiryDate <= CUTOFF
);

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export default function ThisWeeksScoop() {
  // Reads from the SAME managed collection ("events_featured") that the
  // Events & Concerts page writes to — so anything added, edited, or removed
  // there shows up (or disappears) here automatically, no manual syncing.
  const mc = useManagedCollection<any>("events_featured", EVENTS, (e) => slugify(e.id || e.name));

  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();

  // Show every event from the Concerts/Events page that falls in the CURRENT
  // calendar month — not just ones still upcoming — so this always reflects
  // the full month's lineup and rolls forward automatically each month.
  const thisMonthEvents = mc.items
    .filter((e) => isEventInMonth(e, year, month))
    .sort((a, b) => {
      if (!a.date) return -1;
      if (!b.date) return 1;
      return parseLocal(a.date).getTime() - parseLocal(b.date).getTime();
    });

  return (
    <div className="min-h-screen bg-white">
      <CategoryHero
        title="This Week's Dallas Scoop"
        placeholder="Search the scoop…"
      />
      <NewsTicker />

      {/* Main content */}
      <div className="max-w-[1320px] w-[calc(100%-40px)] mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <h2
            className="text-3xl md:text-4xl font-extrabold tracking-tight"
            style={{ fontFamily: "'Poppins', sans-serif", color: "#082f6d" }}
          >
            What's Hot in Dallas This Week
          </h2>
          <p
            className="mt-3 text-gray-600 text-lg max-w-2xl mx-auto"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Your weekly roundup of the hottest events, openings, and buzz across DFW.
          </p>
        </motion.div>

        {/* Upcoming events/concerts happening THIS MONTH — pulled live from the
            Events & Concerts database. Past events (by end date) are auto-hidden. */}
        <div className="mb-12">
          <div className="flex items-center gap-2 mb-4">
            <CalendarDays className="w-5 h-5 text-orange-500" />
            <h3
              className="text-lg font-bold"
              style={{ fontFamily: "'Poppins', sans-serif", color: "#082f6d" }}
            >
              Upcoming Events & Concerts in {MONTH_NAMES[month]}
            </h3>
          </div>
          <p
            className="text-gray-600 mb-6 max-w-3xl"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Discover the latest Desi events and concerts in Dallas-Fort Worth, including Indian and Pakistani events, Bollywood concerts, cultural festivals, dance shows, comedy, food events, community gatherings, Diwali, Holi, Navratri, Dandiya, Garba, Eid celebrations, and more. Find upcoming Dallas events, Frisco events, Plano events, and DFW Desi events all in one place.
          </p>
          {thisMonthEvents.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {thisMonthEvents.map((event, i) => (
                <EventCard key={event._key} event={event as any} index={i} />
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 py-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              No upcoming events or concerts for {MONTH_NAMES[month]} yet — check back soon.
            </p>
          )}
        </div>

        {/* Deals from the Deals page with no expiry, or expiring on/before July 31, 2026 */}
        {HOT_DEALS.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {HOT_DEALS.map((deal, i) => (
              <FeaturedDealCard key={deal._id} deal={deal} index={i} />
            ))}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="text-center py-12 rounded-xl border border-gray-200 bg-gray-50"
          >
            <p
              className="text-gray-500"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              No hot deals right now — check back soon.
            </p>
          </motion.div>
        )}

        {/* Newsletter CTA */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-12 text-center p-8 rounded-2xl"
          style={{
            background: "linear-gradient(135deg, #082f6d 0%, #0a3d8a 100%)",
          }}
        >
          <h3
            className="text-2xl font-bold text-white"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            Never Miss the Scoop
          </h3>
          <p
            className="mt-2 text-white/80"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Get the hottest Dallas updates delivered straight to your inbox every week.
          </p>
          <form
            onSubmit={(e) => e.preventDefault()}
            className="mt-4 flex flex-col sm:flex-row items-center justify-center gap-3 max-w-md mx-auto"
          >
            <input
              type="email"
              placeholder="Your email address"
              className="w-full sm:flex-1 px-4 py-3 rounded-lg text-sm outline-none"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            />
            <button
              type="submit"
              className="px-6 py-3 rounded-lg font-bold text-sm transition-colors duration-200 whitespace-nowrap"
              style={{
                backgroundColor: "#f59e0b",
                color: "#082f6d",
                fontFamily: "'Poppins', sans-serif",
              }}
            >
              Subscribe
            </button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
